import { Controller } from "@hotwired/stimulus"

/**
 * Editor Sidebar — collapse and expand the Drawing v2 left panel.
 *
 * Nothing else. The layer tree and its CRUD live in their own controller; this
 * one owns the 256px ↔ 40px transition and the state the toggle announces.
 *
 * v1 does this with React state (`editor-sidebar-left.tsx:58`), so the
 * behaviour to match is: the aside animates to the stub width, the tab
 * machinery animates to zero and stays in the DOM clipped by overflow-hidden,
 * and the chevron flips 180° over 200ms rather than being swapped for another
 * icon.
 *
 * The two widths arrive as values rather than being hardcoded here, because
 * they are also written into the initial markup by the component — one source,
 * server-side, so the first paint and every later toggle cannot disagree.
 */
export default class extends Controller {
  static targets = ["body", "toggle", "toggleIcon"]
  static values = { openWidth: String, collapsedWidth: String }

  connect() {
    this.collapsed = false
  }

  toggle() {
    this.collapsed = !this.collapsed

    // The aside keeps the stub so the toggle stays clickable; the body goes to
    // nothing, which is what actually hides the tree.
    this.element.classList.toggle(this.openWidthValue, !this.collapsed)
    this.element.classList.toggle(this.collapsedWidthValue, this.collapsed)
    this.bodyTarget.classList.toggle(this.openWidthValue, !this.collapsed)
    this.bodyTarget.classList.toggle("w-0", this.collapsed)

    this.toggleIconTarget.classList.toggle("rotate-180", this.collapsed)

    // The label is a real change of meaning, not decoration: the same button
    // collapses and expands, so a screen reader that kept "Collapse sidebar"
    // would describe the wrong action half the time. Both strings are
    // server-filled — no English reaches this file.
    const { collapseLabel, expandLabel } = this.toggleTarget.dataset
    this.toggleTarget.setAttribute(
      "aria-label",
      this.collapsed ? expandLabel : collapseLabel,
    )
    this.toggleTarget.setAttribute("aria-expanded", String(!this.collapsed))
  }
}
